import { IAppliedFilter } from "@/app/models/auctor"

/**
 * Safely extract query parameters from the current URL in client context
 * Returns null if not in client context or if window is not available
 */
export const getClientQueryParams = (): Record<string, string> | null => {
    if (typeof window === "undefined") {
        return null
    }

    try {
        const searchParams = new URLSearchParams(window.location.search)
        const params: Record<string, string> = {}

        searchParams.forEach((value, key) => {
            params[key] = value
        })

        return params
    } catch (error) {
        console.warn("Error extracting client query parameters:", error)
        return null
    }
}

/**
 * Extract query parameters from a URL string or URL object
 * Safe for server-side usage
 */
export const getQueryParamsFromUrl = (url: string | URL): Record<string, string> => {
    try {
        const urlObj = typeof url === "string" ? new URL(url) : url
        const params: Record<string, string> = {}

        urlObj.searchParams.forEach((value, key) => {
            params[key] = value
        })

        return params
    } catch (error) {
        console.warn("Error extracting query parameters from URL:", error)
        return {}
    }
}

/**
 * Extract query parameters from Next.js searchParams object
 * Safe for server components
 */
export const getQueryParamsFromSearchParams = (
    searchParams: URLSearchParams | { [key: string]: string | string[] | undefined }
): Record<string, string> => {
    const params: Record<string, string> = {}

    try {
        if (searchParams instanceof URLSearchParams) {
            searchParams.forEach((value, key) => {
                params[key] = value
            })
        } else {
            // Handle Next.js searchParams object
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value !== undefined) {
                    // Convert arrays to comma-separated strings
                    params[key] = Array.isArray(value) ? value.join(",") : value
                }
            })
        }
    } catch (error) {
        console.warn("Error processing search parameters:", error)
    }

    return params
}

/**
 * Evaluate a JavaScript expression with applied filters and optional query parameters
 * @param expression - The JavaScript expression to evaluate
 * @param appliedFilters - Array of applied filters
 * @param queryParams - Optional URL query parameters object (e.g., from getClientQueryParams())
 * @returns The result of the expression evaluation
 *
 * @example
 * // In client components:
 * const queryParams = getClientQueryParams();
 * const result = evaluateExpressionWithFilters('return queryParams.utm_source === "google"', filters, queryParams);
 *
 * // In server components:
 * const queryParams = getQueryParamsFromSearchParams(searchParams);
 * const result = evaluateExpressionWithFilters('return queryParams.category || "default"', filters, queryParams);
 */
export const evaluateExpressionWithFilters = (
    expression: string,
    appliedFilters: IAppliedFilter[],
    queryParams?: Record<string, string> | null
) => {
    try {
        expression = prepareExpression(expression)

        expression = populateExpressionWithAppliedFilters(expression, appliedFilters, queryParams)

        const expressionFunction = new Function("appliedFilters", "queryParams", expression)

        return expressionFunction(appliedFilters, queryParams || {})
    } catch (error) {
        console.info(`Error evaluating expression`, expression, error)
        return false
    }
}

export const prepareEvaluableExpression = (
    expression: string,
    appliedFilters: IAppliedFilter[],
    context?: any,
    queryParams?: Record<string, string> | null
): string | null => {
    try {
        let preparedExpression = prepareExpression(expression, context)
        preparedExpression = populateExpressionWithAppliedFilters(preparedExpression, appliedFilters, queryParams)
        return preparedExpression
    } catch (error) {
        console.error(`Error preparing expression: ${expression}`, error)
        return null
    }
}

export const executeExpression = (
    preparedExpression: string | null,
    data: any,
    appliedFilters: IAppliedFilter[],
    queryParams?: Record<string, string> | null
): boolean => {
    if (!preparedExpression) return false // Prevent execution if preparation failed

    try {
        const expressionFunction = new Function("data", "appliedFilters", "queryParams", preparedExpression)
        return expressionFunction(data, appliedFilters, queryParams || {})
    } catch (error) {
        console.error(`Error evaluating the following filter expression: ${preparedExpression}`, error)
        return false
    }
}

/**
 * Evaluate a JavaScript expression with filters, row data, and optional query parameters
 * @param expression - The JavaScript expression to evaluate
 * @param data - The row/item data object
 * @param appliedFilters - Array of applied filters
 * @param context - Optional context string to prepend to the expression
 * @param queryParams - Optional URL query parameters object
 * @returns The result of the expression evaluation
 *
 * @example
 * // Access query parameters in expressions:
 * // "return data.price * (queryParams.discount ? parseFloat(queryParams.discount) / 100 : 1)"
 * // "return queryParams.theme === 'dark' ? 'dark-class' : 'light-class'"
 * // "return data.category === queryParams.selectedCategory"
 */
export const evaluateExpressionWithFiltersAndRowData = (
    expression: string,
    data: any,
    appliedFilters: IAppliedFilter[],
    context?: any,
    queryParams?: Record<string, string> | null
): any => {
    const preparedExpression = prepareEvaluableExpression(expression, appliedFilters, context, queryParams)
    return executeExpression(preparedExpression, data, appliedFilters, queryParams)
}

// a failing expression returns false: never inject it into dangerouslySetInnerHTML
export const toSafeHtml = (evaluatedExpression: any): string => {
    if (evaluatedExpression === null || evaluatedExpression === undefined || typeof evaluatedExpression === "boolean") {
        return ""
    }

    return String(evaluatedExpression)
}

export const prepareExpression = (expression: string, context?: string) => {
    if (!expression.includes("return")) {
        expression = `return ${expression}`
    }

    if (!context) {
        return expression
    }

    return `${context}
        ${expression}`
}

export const populateExpressionWithAppliedFilters = (
    expression: string,
    appliedFilters: IAppliedFilter[],
    queryParams?: Record<string, string> | null
) => {
    let initVariables = ``

    appliedFilters.forEach((filter: any) => {
        initVariables += `
            let ${filter.variableName} = null
        `
    })

    appliedFilters.forEach((filter: any) => {
        initVariables += `
            if (!${filter.variableName}) {
                ${filter.variableName} = appliedFilters.find(aFilter => aFilter.filterId === '${filter.filterId}')
            }`
    })

    return `${initVariables}\n ${expression}`
}

/* 
=== USAGE EXAMPLES ===

1. Client Component Integration:
```tsx
// In a client component (e.g., ResultColumn.tsx)
import { evaluateExpressionWithFiltersAndRowData, getClientQueryParams } from '@/app/utils/expression-utils';

function ResultColumnContent({ column, resultRow, appliedFilters }) {
    // Get query parameters safely in client context
    const queryParams = getClientQueryParams();
    
    return (
        <>
            {column.expression && (
                <div
                    dangerouslySetInnerHTML={{
                        __html: evaluateExpressionWithFiltersAndRowData(
                            column.expression, 
                            resultRow, 
                            appliedFilters,
                            undefined, // context
                            queryParams // now expressions can access queryParams
                        ) ?? '',
                    }}
                />
            )}
        </>
    );
}
```

2. Server Component Integration:
```tsx
// In a server component or API route
import { evaluateExpressionWithFilters, getQueryParamsFromSearchParams } from '@/app/utils/expression-utils';

function ServerComparator({ searchParams, appliedFilters }) {
    // Extract query parameters from Next.js searchParams
    const queryParams = getQueryParamsFromSearchParams(searchParams);
    
    const result = evaluateExpressionWithFilters(
        "return queryParams.utm_source === 'newsletter'",
        appliedFilters,
        queryParams
    );
    
    return <div>{result ? 'Newsletter user' : 'Regular user'}</div>;
}
```

3. Expression Examples Users Can Now Write:
```javascript
// Check if user came from a specific source
"return queryParams.utm_source === 'google'"

// Apply discount based on query parameter
"return data.price * (queryParams.discount ? (1 - parseFloat(queryParams.discount) / 100) : 1)"

// Conditional styling based on theme
"return queryParams.theme === 'dark' ? 'dark-theme-class' : 'light-theme-class'"

// Filter data based on category
"return data.category === queryParams.selectedCategory"

// Complex logic with multiple parameters
"return queryParams.promo === 'summer2024' && data.eligible_for_promo ? data.promo_price : data.regular_price"

// Safe access with fallbacks
"return queryParams.lang || 'fr'"

// Convert string to number safely
"return parseInt(queryParams.maxResults || '10')"
```

4. Backward Compatibility:
All existing expressions continue to work as before. The queryParams parameter is optional,
so existing code doesn't need to be updated unless you want to add query parameter support.

5. Migration Example:
```tsx
// Before (would crash on server):
// User expression: "return new URLSearchParams(window.location.search).get('theme') === 'dark'"

// After (safe for both client and server):
// User expression: "return queryParams.theme === 'dark'"
// Component: pass queryParams = getClientQueryParams() or getQueryParamsFromSearchParams(searchParams)
```
*/
